import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Autocomplete
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const ProcurementItemCreate = () => {
  const navigate = useNavigate();

  const categories = [
    'raw_materials',
    'equipment',
    'tools',
    'electrical',
    'plumbing',
    'hvac',
    'finishes',
    'structural',
    'site',
    'safety'
  ];
  const units = ['pcs', 'kg', 'lbs', 'ft', 'm', 'sqft', 'sqm', 'gal', 'L', 'box', 'roll', 'sheet', 'bag', 'ton'];
  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'discontinued', label: 'Discontinued' }
  ];

  const [vendors, setVendors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    subcategory: '',
    specifications: {
      material: '',
      dimensions: '',
      weight: '',
      color: '',
      brand: '',
      model: ''
    },
    preferredVendors: [],
    pricing: {
      currentPrice: 0,
      currency: 'USD',
      priceHistory: []
    },
    inventory: {
      currentStock: 0,
      unit: 'pcs',
      minimumStock: 10,
      maximumStock: 1000,
      reorderPoint: 20,
      location: ''
    },
    usage: {
      averageMonthlyUsage: 0,
      lastUsedDate: null,
      totalUsed: 0
    },
    compliance: {
      certifications: [],
      standards: [],
      safetyRequirements: ''
    },
    qualityControl: {
      inspectionRequired: false,
      qualityStandards: '',
      testingProcedures: ''
    },
    status: 'active',
    notes: ''
  });

  useEffect(() => {
    const loadVendors = async () => {
      try {
        const [vendorsRes, companiesRes] = await Promise.all([
          api.get('/vendors?status=active'),
          api.get('/companies?limit=100')
        ]);
        setVendors(vendorsRes.data || []);
        const list = (companiesRes.data && companiesRes.data.data) || companiesRes.data || [];
        setCompanies(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Error loading vendors:', err);
        setError('Failed to load vendors');
      }
    };
    loadVendors();
  }, []);

  const effectiveVendors = selectedCompany
    ? vendors.filter((v) => (v.companyName || '').toLowerCase() === selectedCompany.toLowerCase())
    : vendors;

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async () => {
    try {
      await api.post('/procurement-items', formData);
      setSuccess('Procurement item created successfully');
      navigate('/procurement-items');
    } catch (err) {
      console.error('Error saving item:', err);
      // Surface detailed backend validation/processing errors in the UI
      const payload = err?.response?.data;
      const baseMessage = payload?.message || (typeof payload === 'string' ? payload : '') || err?.message || 'Failed to save procurement item';
      const details = Array.isArray(payload?.errors) ? payload.errors : [];
      const message = details.length ? `${baseMessage}${baseMessage?.endsWith('.') ? '' : ':'} ${details.join(', ')}` : baseMessage;
      setError(message);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">Add Procurement Item</Typography>
        <Button variant="outlined" onClick={() => navigate('/procurement-items')}>Back to Items</Button>
      </Box>
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => handleInputChange('category', e.target.value)}
                >
                  {categories.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Subcategory"
                value={formData.subcategory}
                onChange={(e) => handleInputChange('subcategory', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={companies}
                getOptionLabel={(option) => option.name || ''}
                value={companies.find((c) => c.name === selectedCompany) || null}
                onChange={(event, newValue) => setSelectedCompany(newValue?.name || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Company Name" placeholder="Select company to filter vendors" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={effectiveVendors}
                getOptionLabel={(option) => option.name}
                value={effectiveVendors.filter(v => formData.preferredVendors.includes(v._id))}
                onChange={(event, newValue) => {
                  handleInputChange('preferredVendors', newValue.map(v => v._id));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Preferred Vendors" placeholder="Select preferred vendors" />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Specifications</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Material"
                value={formData.specifications.material}
                onChange={(e) => handleInputChange('specifications.material', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Dimensions"
                value={formData.specifications.dimensions}
                onChange={(e) => handleInputChange('specifications.dimensions', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Weight"
                value={formData.specifications.weight}
                onChange={(e) => handleInputChange('specifications.weight', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Color"
                value={formData.specifications.color}
                onChange={(e) => handleInputChange('specifications.color', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Brand"
                value={formData.specifications.brand}
                onChange={(e) => handleInputChange('specifications.brand', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Model"
                value={formData.specifications.model}
                onChange={(e) => handleInputChange('specifications.model', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Inventory</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Current Stock"
                type="number"
                value={formData.inventory.currentStock}
                onChange={(e) => handleInputChange('inventory.currentStock', parseInt(e.target.value) || 0)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={formData.inventory.unit}
                  label="Unit"
                  onChange={(e) => handleInputChange('inventory.unit', e.target.value)}
                >
                  {units.map((u) => (
                    <MenuItem key={u} value={u}>{u}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Minimum Stock"
                type="number"
                value={formData.inventory.minimumStock}
                onChange={(e) => handleInputChange('inventory.minimumStock', parseInt(e.target.value) || 0)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Maximum Stock"
                type="number"
                value={formData.inventory.maximumStock}
                onChange={(e) => handleInputChange('inventory.maximumStock', parseInt(e.target.value) || 0)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reorder Point"
                type="number"
                value={formData.inventory.reorderPoint}
                onChange={(e) => handleInputChange('inventory.reorderPoint', parseInt(e.target.value) || 0)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Storage Location"
                value={formData.inventory.location}
                onChange={(e) => handleInputChange('inventory.location', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Pricing</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Current Price"
                type="number"
                value={formData.pricing.currentPrice}
                onChange={(e) => handleInputChange('pricing.currentPrice', parseFloat(e.target.value) || 0)}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={formData.pricing.currency}
                  label="Currency"
                  onChange={(e) => handleInputChange('pricing.currency', e.target.value)}
                >
                  {['USD','EUR','GBP','JPY','CAD','AUD'].map((cur) => (
                    <MenuItem key={cur} value={cur}>{cur}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="outlined" sx={{ mr: 2 }} onClick={() => navigate('/procurement-items')}>Cancel</Button>
              <Button variant="contained" onClick={handleSubmit}>Add Item</Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ProcurementItemCreate;